import React from 'react';
import clsx from 'clsx';
import { ColorInput } from '~/components/library/color-input';
import { normalizeHex6 } from '~/components/library/lib/color';
import { ChevronDownIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import {
  BrandPalette,
  BrandColorScheme,
  samePalette,
  PaletteRole,
  READABLE,
  betterColors,
  contrastRatio,
  scoreFor,
  scoreLabel,
  StripeFill,
  usageScore,
  usagesFor
} from './design-brand';

/** The color scheme whose palette this is, or undefined for one somebody set by hand. */
export const matchingColorScheme = (colorSchemes: BrandColorScheme[], palette: BrandPalette) =>
  colorSchemes.find((scheme) => samePalette(scheme.palette, palette));

export const ColorSchemeTile = ({
  scheme,
  active,
  onPick
}: {
  scheme: BrandColorScheme;
  active: boolean;
  onPick: () => void;
}) => (
  <button
    type="button"
    onClick={onPick}
    title={scheme.note}
    className={clsx(
      'rounded-lg border p-1.5 text-left transition',
      active
        ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-300 dark:bg-sky-500/10 dark:ring-sky-700'
        : 'border-neutral-200 hover:bg-neutral-50 dark:border-neutral-700 dark:hover:bg-white/5'
    )}
  >
    <span className="flex h-6 overflow-hidden rounded border border-black/10 dark:border-white/10">
      {(['primaryHex', 'secondaryHex', 'accentHex', 'textHex'] as const).map((role) => (
        <span key={role} className="flex-1" style={{ background: scheme.palette[role] }} />
      ))}
    </span>
    <span className="mt-1 block text-xs font-medium text-neutral-800 dark:text-neutral-100">
      {scheme.name}
    </span>
  </button>
);

/** One color, wearing itself. */
export const ColorRow = ({
  role,
  palette,
  stripeFill,
  open,
  onToggle,
  onChange
}: {
  role: PaletteRole;
  palette: BrandPalette;
  stripeFill: StripeFill;
  open: boolean;
  onToggle: () => void;
  onChange: (palette: BrandPalette) => void;
}) => {
  const hex = palette[role.key];
  const [typedHex, setTypedHex] = React.useState(hex);
  React.useEffect(() => setTypedHex(hex), [hex]);
  const commitHex = () => {
    const text = typedHex.trim().replace(/^#/, '');
    if (!/^(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(text)) {
      setTypedHex(hex);
      return;
    }
    const canonical =
      '#' + (text.length === 3 ? [...text].map((c) => c + c).join('') : text).toLowerCase();
    setTypedHex(canonical);
    if (canonical !== hex) set(canonical);
  };
  const score = scoreFor(role.key, palette, stripeFill);
  const set = (next: string) => onChange({ ...palette, [role.key]: next });
  const good = score >= READABLE;

  return (
    <div
      className={clsx(
        'rounded-lg border',
        open ? 'border-neutral-300 dark:border-neutral-600' : 'border-transparent'
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-label={`${role.label}, ${score} out of 100`}
        className="flex w-full items-center gap-2.5 rounded-lg px-1.5 py-1.5 text-left hover:bg-neutral-50 dark:hover:bg-white/5"
      >
        <span
          aria-hidden
          className="size-8 shrink-0 rounded-md border border-black/10 dark:border-white/10"
          style={{ background: hex }}
        />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-neutral-800 dark:text-neutral-100">
            {role.label}
          </span>
          <span className="block truncate text-xs text-neutral-500 dark:text-neutral-400">
            {role.description}
          </span>
        </span>
        <span
          aria-hidden
          className={clsx(
            'flex shrink-0 items-center gap-0.5 text-xs font-semibold tabular-nums',
            good ? 'text-neutral-500 dark:text-neutral-400' : 'text-red-600 dark:text-red-400'
          )}
        >
          {!good && <ExclamationTriangleIcon className="size-3.5" />}
          {score}
        </span>
        <ChevronDownIcon
          className={clsx(
            'size-4 shrink-0 text-neutral-400 transition dark:text-neutral-500',
            open && 'rotate-180'
          )}
        />
      </button>

      {open && (
        <div className="px-1.5 pb-2">
          <ColorInput
            label={role.label}
            spellCheck={false}
            value={hex}
            textValue={typedHex}
            inputClassName="w-full"
            onChange={(next) => {
              setTypedHex(next ?? '');
              if (next && normalizeHex6(next) === next && next !== hex) set(next);
            }}
            onTextBlur={commitHex}
            onTextKeyDown={(e) => {
              if (e.key === 'Enter') commitHex();
            }}
          />

          <ScoreCard role={role} palette={palette} stripeFill={stripeFill} score={score} />
          <Suggestions role={role} palette={palette} stripeFill={stripeFill} onPick={set} />
        </div>
      )}
    </div>
  );
};

const ScoreCard = ({
  role,
  palette,
  stripeFill,
  score
}: {
  role: PaletteRole;
  palette: BrandPalette;
  stripeFill: StripeFill;
  score: number;
}) => {
  const good = score >= READABLE;

  return (
    <div className="mt-3">
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-medium text-neutral-900 dark:text-white">
          How it works with your palette
        </span>
        <span
          className={clsx(
            'text-lg font-bold tabular-nums',
            good ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'
          )}
        >
          {score}
        </span>
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-700">
        <div
          className={clsx('h-full rounded-full', good ? 'bg-green-600' : 'bg-red-500')}
          style={{ width: `${score}%` }}
        />
      </div>
      <p
        className={clsx(
          'mt-1.5 text-xs font-medium',
          good ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'
        )}
      >
        {scoreLabel(score)}
      </p>

      <ul className="mt-2 flex flex-col gap-1 border-t border-neutral-100 pt-2 dark:border-neutral-700">
        {usagesFor(role.key, palette, stripeFill).map((usage) => {
          const each = usageScore(usage);
          return (
            <li
              key={usage.where}
              className="flex items-center justify-between gap-3 text-xs"
              // The ratio is kept where somebody who wants it can find it, and out of the way
              // of everybody who does not.
              title={`${contrastRatio(usage.foregroundHex, usage.backgroundHex).toFixed(1)}:1`}
            >
              <span className="text-neutral-600 dark:text-neutral-400">{usage.where}</span>
              <span
                className={clsx(
                  'shrink-0 font-medium',
                  each >= READABLE
                    ? 'text-neutral-500 dark:text-neutral-400'
                    : 'text-red-600 dark:text-red-400'
                )}
              >
                {each >= READABLE ? 'Clear' : 'Hard to see'}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const Suggestions = ({
  role,
  palette,
  stripeFill,
  onPick
}: {
  role: PaletteRole;
  palette: BrandPalette;
  stripeFill: StripeFill;
  onPick: (hex: string) => void;
}) => {
  const better = betterColors(role.key, palette, stripeFill);
  if (better.length === 0) {
    return null;
  }

  return (
    <div className="mt-3">
      <p className="text-xs font-medium text-neutral-800 dark:text-neutral-100">Try instead</p>
      <div className="mt-1.5 flex flex-col gap-1">
        {better.map((option) => (
          <button
            key={option.hex}
            type="button"
            onClick={() => onPick(option.hex)}
            className="flex items-center gap-2 rounded-md border border-neutral-200 px-2 py-1.5 text-left hover:bg-neutral-50 dark:border-neutral-700 dark:hover:bg-white/5"
          >
            <span
              className="size-4 shrink-0 rounded border border-black/10 dark:border-white/10"
              style={{ background: option.hex }}
            />
            <span className="font-mono text-xs uppercase text-neutral-700 dark:text-neutral-300">
              {option.hex}
            </span>
            <span className="ml-auto text-xs font-semibold tabular-nums text-green-700 dark:text-green-400">
              {option.score}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};
